import { useEffect, useState } from "react"

import { fetchDocument } from "../api/signing.services"
import type { SigningRefusal } from "../types/signing.types"

interface SigningDocument {
  url: string | null
  blob: Blob | null
  refusal: SigningRefusal | null
  loading: boolean
}

interface Held {
  session: string
  url: string | null
  blob: Blob | null
  refusal: SigningRefusal | null
}

// One fetch per sitting, since each is an audited opening; what is held belongs to its session.
export const useSigningDocument = (
  session: string | null,
  enabled: boolean
): SigningDocument => {
  const [held, setHeld] = useState<Held | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!session || !enabled) return
    let created: string | null = null
    let abandoned = false
    const refuse = (refusal: SigningRefusal) =>
      setHeld({ session, url: null, blob: null, refusal })

    setLoading(true)
    void fetchDocument(session)
      .then((response) => {
        if (abandoned) {
          if (response.data) URL.revokeObjectURL(response.data)
          return
        }
        if (!response.ok || !response.data) {
          refuse(response.refusal ?? { reason: "unknown" })
          return
        }
        created = response.data
        setHeld({
          session,
          url: response.data,
          blob: response.blob ?? null,
          refusal: null
        })
      })
      .catch(() => {
        if (!abandoned) refuse({ reason: "unknown" })
      })
      .finally(() => {
        if (!abandoned) setLoading(false)
      })

    return () => {
      abandoned = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [session, enabled])

  const mine = held?.session === session ? held : null
  return {
    url: mine?.url ?? null,
    blob: mine?.blob ?? null,
    refusal: mine?.refusal ?? null,
    loading
  }
}
